import { hasSupabaseEnv, supabaseClient } from './supabase';

/** Whitelisted branding fields safe for UI (matches shared.public_branding_json). */
export const SAFE_BRANDING_KEYS = [
  'product_name',
  'tagline',
  'logo_url',
  'logo_dark_url',
  'favicon_url',
  'primary_color',
  'secondary_color',
  'accent_color',
  'background_color',
  'login_message',
  'support_url',
] as const;

export type SafeBrandingKey = (typeof SAFE_BRANDING_KEYS)[number];

export interface PlatformBranding {
  product_name?: string;
  tagline?: string;
  logo_url?: string;
  logo_dark_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  background_color?: string;
  login_message?: string;
  support_url?: string;
}

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  display_name: string;
  tenant_type?: string;
  geofips?: string | null;
  location_name?: string | null;
  county?: string | null;
  state?: string | null;
  timezone?: string | null;
}

export interface ResolveDomainResult {
  tenant_id: string;
  app_slug: string;
  enabled: boolean;
  tenant: PlatformTenant | null;
  branding: PlatformBranding;
}

export interface TenantAppAccess {
  app_slug: string;
  name?: string;
  description?: string;
  default_path?: string;
  tier: string;
  settings?: Record<string, unknown>;
  enabled?: boolean;
}

/** CoolClimate Network platform root tenant (seed UUID). */
export const PLATFORM_ROOT_TENANT_ID = '00000000-0000-4000-8000-000000000001';

/** Local/dev launcher when RPC returns no rows (localhost fallback). */
export const FALLBACK_PORTAL_APPS: TenantAppAccess[] = [
  {
    app_slug: 'climateplans',
    name: 'ClimatePlans',
    description: 'California climate planning data, GHG inventories, and policy tools for cities and counties.',
    default_path: '/',
    tier: 'free',
  },
  {
    app_slug: 'calculator',
    name: 'Household Calculator',
    description: 'Estimate household carbon footprints and explore reduction actions.',
    default_path: '/coolclimate',
    tier: 'free',
  },
  {
    app_slug: 'business',
    name: 'Business Calculator',
    description: 'Business carbon footprint and procurement analysis tools.',
    default_path: '/portal',
    tier: 'standard',
  },
  {
    app_slug: 'challenge',
    name: 'Cool Campus Challenge',
    description: 'Campus engagement competitions and sustainability programs.',
    default_path: '/portal',
    tier: 'standard',
  },
  {
    app_slug: 'events',
    name: 'Conferences & Events',
    description: 'Conference communities, sessions, and attendee engagement.',
    default_path: '/portal',
    tier: 'standard',
  },
  {
    app_slug: 'developer',
    name: 'Developer Platform',
    description: 'API keys, documentation, and developer resources.',
    default_path: '/changelog?tab=developers-log',
    tier: 'standard',
  },
];

export interface LoadedPlatformContext {
  tenantId: string | null;
  appSlug: string;
  enabled: boolean;
  tenant: PlatformTenant | null;
  branding: PlatformBranding;
  availableApps: TenantAppAccess[];
  usedFallback: boolean;
  hostname: string;
}

export interface LogActivityParams {
  appSlug?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  metadata?: Record<string, unknown>;
  tenantId?: string | null;
  userId?: string | null;
}

const DEFAULT_BRANDING: PlatformBranding = {
  product_name: 'CoolClimate',
  tagline: 'Tools for a cooler planet',
  primary_color: '#2E7D32',
  secondary_color: '#1B5E20',
  accent_color: '#66BB6A',
  background_color: '#ffffff',
  login_message: 'Welcome to the CoolClimate ecosystem.',
};

const CSS_VAR_MAP: Record<'primary_color' | 'secondary_color' | 'accent_color' | 'background_color', string> = {
  primary_color: '--cc-primary-color',
  secondary_color: '--cc-secondary-color',
  accent_color: '--cc-accent-color',
  background_color: '--cc-background-color',
};

const HOST_APP_SLUG: Record<string, string> = {
  'coolclimate.org': 'portal',
  'www.coolclimate.org': 'portal',
  'climateplans.org': 'climateplans',
  'www.climateplans.org': 'climateplans',
  'calculator.coolclimate.org': 'calculator',
  'events.coolclimate.org': 'events',
  'apps.coolclimate.org': 'developer',
};

function devWarn(message: string, detail?: unknown): void {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[platform] ${message}`, detail ?? '');
  }
}

function isSafeHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Strip unknown keys; never pass custom_css, footer_html, or email fields to UI. */
export function pickSafeBranding(raw: unknown): PlatformBranding {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_BRANDING };
  }
  const source = raw as Record<string, unknown>;
  const branding: PlatformBranding = {};
  for (const key of SAFE_BRANDING_KEYS) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      branding[key] = value.trim();
    }
  }
  return Object.keys(branding).length ? branding : { ...DEFAULT_BRANDING };
}

function parseTenant(raw: unknown): PlatformTenant | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.id !== 'string' || typeof t.slug !== 'string') return null;
  return {
    id: t.id,
    slug: t.slug,
    name: typeof t.name === 'string' ? t.name : t.slug,
    display_name: typeof t.display_name === 'string' ? t.display_name : (typeof t.name === 'string' ? t.name : t.slug),
    tenant_type: typeof t.tenant_type === 'string' ? t.tenant_type : undefined,
    geofips: typeof t.geofips === 'string' ? t.geofips : null,
    location_name: typeof t.location_name === 'string' ? t.location_name : null,
    county: typeof t.county === 'string' ? t.county : null,
    state: typeof t.state === 'string' ? t.state : null,
    timezone: typeof t.timezone === 'string' ? t.timezone : null,
  };
}

export function inferAppSlug(hostname: string, pathname: string): string {
  const host = hostname.toLowerCase();
  if (HOST_APP_SLUG[host]) {
    return HOST_APP_SLUG[host];
  }
  const path = pathname.toLowerCase();
  if (path.startsWith('/coolclimate') || path.includes('calculator')) {
    return 'calculator';
  }
  const envSlug = process.env.REACT_APP_PLATFORM_APP_SLUG;
  if (envSlug) {
    return envSlug;
  }
  return 'climateplans';
}

export function applyBranding(branding: PlatformBranding): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  (Object.keys(CSS_VAR_MAP) as Array<keyof typeof CSS_VAR_MAP>).forEach((key) => {
    const cssVar = CSS_VAR_MAP[key];
    const value = branding[key];
    if (value) {
      root.style.setProperty(cssVar, value);
    } else {
      root.style.removeProperty(cssVar);
    }
  });

  if (branding.favicon_url && isSafeHttpUrl(branding.favicon_url)) {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = branding.favicon_url;
  }
}

export async function resolveDomain(hostname: string): Promise<ResolveDomainResult | null> {
  if (!hasSupabaseEnv()) {
    devWarn('Supabase env missing; skipping resolve_domain');
    return null;
  }

  try {
    const { data, error } = await supabaseClient.rpc('platform_resolve_domain', { p_hostname: hostname });

    if (error) {
      devWarn('platform_resolve_domain RPC failed', error);
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row || typeof row !== 'object') {
      return null;
    }

    const record = row as Record<string, unknown>;
    if (typeof record.tenant_id !== 'string' || typeof record.app_slug !== 'string') {
      return null;
    }

    return {
      tenant_id: record.tenant_id,
      app_slug: record.app_slug,
      enabled: record.enabled === true,
      tenant: parseTenant(record.tenant),
      branding: pickSafeBranding(record.branding),
    };
  } catch (err) {
    devWarn('resolve_domain exception', err);
    return null;
  }
}

export async function getAvailableApps(tenantId: string | null): Promise<TenantAppAccess[]> {
  if (!tenantId || !hasSupabaseEnv()) {
    return [];
  }

  try {
    const { data, error } = await supabaseClient.rpc('platform_get_available_apps', {
      p_tenant_id: tenantId,
    });

    if (error) {
      devWarn('platform_get_available_apps failed', error);
      return [];
    }

    return (data ?? []).map((row: Record<string, unknown>) => {
      return {
        app_slug: String(row.app_slug ?? ''),
        name: typeof row.name === 'string' ? row.name : undefined,
        description: typeof row.description === 'string' ? row.description : undefined,
        default_path: typeof row.default_path === 'string' ? row.default_path : undefined,
        tier: String(row.tier ?? 'standard'),
        settings: typeof row.settings === 'object' && row.settings !== null
          ? (row.settings as Record<string, unknown>)
          : undefined,
        enabled: true,
      } satisfies TenantAppAccess;
    }).filter((app: TenantAppAccess) => app.app_slug);
  } catch (err) {
    devWarn('getAvailableApps exception', err);
    return [];
  }
}

export async function loadPlatformContext(pathname?: string): Promise<LoadedPlatformContext> {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  const resolved = await resolveDomain(hostname);

  if (resolved?.enabled) {
    applyBranding(resolved.branding);
    const availableApps = await getAvailableApps(resolved.tenant_id);
    return {
      tenantId: resolved.tenant_id,
      appSlug: resolved.app_slug,
      enabled: true,
      tenant: resolved.tenant,
      branding: resolved.branding,
      availableApps,
      usedFallback: false,
      hostname,
    };
  }

  const appSlug = resolved?.app_slug ?? inferAppSlug(hostname, path);
  const branding = { ...DEFAULT_BRANDING };
  applyBranding(branding);

  if (resolved && !resolved.enabled) {
    devWarn('Domain resolved but app access disabled; using fallback branding', { hostname, appSlug });
  } else if (!resolved) {
    devWarn('No domain match; using fallback branding', { hostname, appSlug });
  }

  return {
    tenantId: resolved?.tenant_id ?? null,
    appSlug,
    enabled: resolved?.enabled ?? false,
    tenant: resolved?.tenant ?? null,
    branding,
    availableApps: resolved?.tenant_id ? await getAvailableApps(resolved.tenant_id) : [],
    usedFallback: true,
    hostname,
  };
}

export async function logActivity(params: LogActivityParams): Promise<void> {
  if (!hasSupabaseEnv()) return;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  const path = typeof window !== 'undefined' ? window.location.pathname : undefined;

  try {
    const { error } = await supabaseClient.rpc('platform_log_activity', {
      p_action: params.action,
      p_tenant_id: params.tenantId ?? null,
      p_app_slug: params.appSlug ?? 'climateplans',
      p_resource_type: params.resourceType ?? null,
      p_resource_id: params.resourceId ?? null,
      p_metadata: {
        hostname,
        path,
        ...(params.metadata ?? {}),
      },
    });
    if (error) {
      devWarn('platform_log_activity failed', error);
    }
  } catch (err) {
    devWarn('activity_log insert exception', err);
  }
}

export { DEFAULT_BRANDING };
